/**
 * ============================================================================
 * LINK PREVIEW COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Preview of transaction link with metadata
 */

import React from 'react';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import StatusBadge from '../common/StatusBadge';
import Avatar from '../common/Avatar';

/**
 * Link Preview Component
 * @param {Object} props
 * @param {Object} props.transaction - Transaction object
 * @param {string} props.shortLink - Short link URL
 * @returns {JSX.Element}
 */
const LinkPreview = ({ transaction, shortLink }) => {
  const { formatCurrency, formatDate, language } = useI18n();

  if (!transaction) return null;

  const getStatusVariant = (status) => {
    const variants = {
      created: 'default',
      link_sent: 'info',
      payment_pending: 'warning',
      escrow_funded: 'success',
      in_progress: 'info',
      delivered: 'success',
      completed: 'success',
      disputed: 'error',
      cancelled: 'default',
    };
    return variants[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      created: language === 'fr' ? 'Créé' : 'Created',
      link_sent: language === 'fr' ? 'Lien envoyé' : 'Link Sent',
      payment_pending: language === 'fr' ? 'Paiement en attente' : 'Payment Pending',
      escrow_funded: language === 'fr' ? 'Fonds en escrow' : 'Funded',
      in_progress: language === 'fr' ? 'En cours' : 'In Progress',
      delivered: language === 'fr' ? 'Livré' : 'Delivered',
      completed: language === 'fr' ? 'Complété' : 'Completed',
      disputed: language === 'fr' ? 'En litige' : 'Disputed',
      cancelled: language === 'fr' ? 'Annulé' : 'Cancelled',
    };
    return labels[status] || status;
  };

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">🔗</span>
            {language === 'fr' ? 'Aperçu du lien' : 'Link Preview'}
          </h3>
          <StatusBadge variant={getStatusVariant(transaction.status)} size="sm">
            {getStatusLabel(transaction.status)}
          </StatusBadge>
        </div>

        {/* Transaction Title */}
        <div>
          <h4 className="text-xl font-black text-gray-900 dark:text-white mb-2">
            {transaction.title}
          </h4>
          {transaction.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
              {transaction.description}
            </p>
          )}
        </div>

        {/* Link Display */}
        {shortLink && (
          <Card variant="outlined" className="bg-gray-50 dark:bg-gray-800/50 p-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">🔗</span>
              <code className="flex-1 text-sm font-mono text-gray-700 dark:text-gray-300 truncate">
                {shortLink}
              </code>
            </div>
          </Card>
        )}

        {/* Transaction Details */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Montant' : 'Amount'}
            </p>
            <p className="text-lg font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Référence' : 'Reference'}
            </p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">
              {transaction.referenceId || transaction.shortCode || 'N/A'}
            </p>
          </div>
        </div>

        {/* Parties */}
        {(transaction.creator || transaction.counterparty) && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              {language === 'fr' ? 'Parties' : 'Parties'}
            </p>
            <div className="flex items-center gap-3">
              {transaction.creator && (
                <div className="flex items-center gap-2">
                  <Avatar
                    src={transaction.creator.avatar}
                    name={transaction.creator.name || transaction.creator.email}
                    size="sm"
                  />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {transaction.creator.name || transaction.creator.email}
                  </span>
                </div>
              )}
              {transaction.counterparty && (
                <>
                  <span className="text-gray-400">→</span>
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={transaction.counterparty.avatar}
                      name={transaction.counterparty.name || transaction.counterparty.email}
                      size="sm"
                    />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {transaction.counterparty.name || transaction.counterparty.email}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Created Date */}
        {transaction.createdAt && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'fr' ? 'Créé le' : 'Created on'} {formatDate(transaction.createdAt)}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default LinkPreview;
